// ============================================================
// Profileur : état d'un serveur et voyant (vert, orange, rouge)
// ============================================================
// Fonctions pures (testées par profiler.health.test.ts). Deux questions séparées :
//
// 1. Les mesures arrivent-elles ? Un serveur dont le profileur est éteint, en pause, muet ou jamais
//    vu n'est PAS en panne : on ne sait rien de sa santé, il s'affiche en gris avec la raison.
// 2. Si elles arrivent, le monde tient-il son rythme ? Un monde fait 20 ticks par seconde : chaque
//    tick a 50 ms pour finir. Le voyant ne juge que cela, avec des seuils nommés ci-dessous et
//    rappelés dans la page.
// ============================================================
import type { LevelSummary, ServerSummary } from './profiler.types';
import { fmtClock, fmtMs, fmtNumber, fmtTps, fmtWhen, plural } from './profiler.format.ts';

/** Temps dont dispose un tick : 1 seconde divisée par 20 ticks. */
export const TICK_BUDGET_MS = 50;
/** Rythme normal d'un monde. */
export const TARGET_TPS = 20;
/** Durée au-delà de laquelle le moteur signale un tick (réglage par défaut d'Endium). */
export const SPIKE_THRESHOLD_MS = 200;

/** Un instantané par minute est attendu : au-delà de trois minutes, le serveur est « sans nouvelles ». */
export const STALE_AFTER_MS = 3 * 60_000;
/**
 * Temps après son démarrage pendant lequel un serveur n'est pas jugé, quand le satellite ne le dit pas
 * (ServerList.startupGraceSeconds) : il charge son monde, ses ticks longs ne disent rien de sa santé.
 */
export const DEFAULT_STARTUP_GRACE_S = 300;
/** Profileur éteint : un message toutes les dix minutes. */
export const STALE_OFF_AFTER_MS = 25 * 60_000;

/** Seuils du voyant. Rouge : le jeu est ralenti ou figé. Orange : il tient, mais de peu, ou il a accroché. */
export const THRESHOLDS = {
  /** Rouge : un tick en cours depuis plus longtemps que cela (le monde est figé). */
  stuckMs: 5_000,
  /** Rouge : moins de ticks par seconde que cela (le jeu perd plus de 10 % de sa vitesse). */
  badTps: 18,
  /** Orange : moins de ticks par seconde que cela. */
  watchTps: 19.5,
  /** Rouge : durée moyenne d'un tick au-dessus du budget. */
  badAverageMs: TICK_BUDGET_MS,
  /** Orange : durée moyenne au-dessus de la moitié du budget. */
  watchAverageMs: TICK_BUDGET_MS / 2,
  /** Rouge : retard à rattraper au-delà d'une seconde. */
  badLagMs: 1_000,
  /** Orange : retard au-delà d'un dixième de seconde. */
  watchLagMs: 100,
  /** Rouge : autant de ticks trop longs dans la dernière heure (un toutes les deux minutes). */
  badSpikesPerHour: 30,
  /** Rouge : un tick de la dernière heure a duré plus que cela (le monde s'est figé). */
  badFreezeMs: 5_000,
} as const;

/**
 * measuring : les mesures arrivent. off : profileur éteint sur le serveur. paused : mesures suspendues
 * (commande profiler off). stale : plus rien depuis un moment. never : connu de la Console, jamais vu ici.
 */
export type ServerState = 'measuring' | 'off' | 'paused' | 'stale' | 'never';

/** ok : vert. watch : orange. bad : rouge. none : gris, pas de mesure à juger. */
export type Light = 'ok' | 'watch' | 'bad' | 'none';

export interface Health {
  state: ServerState;
  light: Light;
  /** Deux ou trois mots : « Tout va bien », « Monde figé », « Profileur éteint ». */
  title: string;
  /** La raison, en une phrase, avec les chiffres. */
  reason: string;
  /** Monde qui décide du voyant, quand il y en a plusieurs. */
  level: string | null;
}

const RANK: Record<Light, number> = { none: 0, ok: 1, watch: 2, bad: 3 };

/** Les mesures arrivent-elles ? `now` est l'horloge du satellite (ServerList.now). */
export function stateOf(server: Pick<ServerSummary, 'enabled' | 'paused' | 'lastSnapshotAt'>, now: number): ServerState {
  if (server.lastSnapshotAt == null || server.enabled == null) return 'stale';
  const age = now - server.lastSnapshotAt;
  if (!server.enabled) return age > STALE_OFF_AFTER_MS ? 'stale' : 'off';
  if (age > STALE_AFTER_MS) return 'stale';
  return server.paused ? 'paused' : 'measuring';
}

/**
 * Le serveur vient-il de démarrer ? Vrai pendant ses premières minutes : le chargement du monde y fait
 * des ticks longs, que le satellite écarte lui aussi des compteurs de l'heure.
 */
export function startingUp(server: Pick<ServerSummary, 'startedAt'>, now: number, graceSeconds: number = DEFAULT_STARTUP_GRACE_S): boolean {
  if (server.startedAt == null) return false;
  const age = now - server.startedAt;
  return age < graceSeconds * 1000;
}

/** Verdict d'un monde sur sa dernière minute. */
export function levelHealth(level: LevelSummary): { light: Light; title: string; reason: string } {
  // Avant tout le reste : sans mesure, « aucun tick » ne dit rien du monde, qui tourne toujours.
  if (level.measuresStopped) {
    return {
      light: 'none', title: 'Mesures arrêtées',
      reason: "Le profileur a cessé de mesurer ce monde après une erreur de sa part (elle est dans le journal du serveur). "
        + "Le monde tourne toujours ; les mesures reprennent au redémarrage du serveur.",
    };
  }
  if (level.tickInProgress && level.tickInProgressMs >= THRESHOLDS.stuckMs) {
    return { light: 'bad', title: 'Monde figé', reason: `Un tick est en cours depuis ${fmtMs(level.tickInProgressMs)} : le monde n'avance plus.` };
  }
  if (level.ticks <= 0) {
    return level.tickInProgress
      ? { light: 'bad', title: 'Monde figé', reason: `Aucun tick fini sur la dernière minute, un tick est en cours depuis ${fmtMs(level.tickInProgressMs)}.` }
      : { light: 'watch', title: 'Aucun tick', reason: 'Aucun tick fini sur la dernière minute.' };
  }
  if (level.ticksPerSecond < THRESHOLDS.badTps) {
    return { light: 'bad', title: 'Jeu ralenti', reason: `${fmtTps(level.ticksPerSecond)} ticks par seconde au lieu de ${TARGET_TPS} : le jeu tourne au ralenti.` };
  }
  if (level.averageTickMs >= THRESHOLDS.badAverageMs) {
    return {
      light: 'bad', title: 'Budget dépassé',
      reason: `Un tick prend ${fmtMs(level.averageTickMs)} en moyenne, plus que les ${TICK_BUDGET_MS} ms disponibles.`,
    };
  }
  if (level.lagMs >= THRESHOLDS.badLagMs) {
    return { light: 'bad', title: 'Monde en retard', reason: `Le monde a ${fmtMs(level.lagMs)} de retard à rattraper.` };
  }
  if (level.averageTickMs >= THRESHOLDS.watchAverageMs) {
    return {
      light: 'watch', title: 'Peu de marge',
      reason: `Un tick prend ${fmtMs(level.averageTickMs)} en moyenne : plus de la moitié des ${TICK_BUDGET_MS} ms disponibles.`,
    };
  }
  if (level.ticksPerSecond < THRESHOLDS.watchTps) {
    return { light: 'watch', title: 'Un peu lent', reason: `${fmtTps(level.ticksPerSecond)} ticks par seconde au lieu de ${TARGET_TPS}.` };
  }
  if (level.lagMs >= THRESHOLDS.watchLagMs) {
    return { light: 'watch', title: 'Léger retard', reason: `Le monde a ${fmtMs(level.lagMs)} de retard à rattraper.` };
  }
  if (level.spikes > 0) {
    return {
      light: 'watch', title: 'Accroche récente',
      reason: `${plural(level.spikes, 'tick trop long', 'ticks trop longs')} sur la dernière minute (le plus long : ${fmtMs(level.maxTickMs)}).`,
    };
  }
  return {
    light: 'ok', title: 'Tout va bien',
    reason: `Un tick prend ${fmtMs(level.averageTickMs)} sur les ${TICK_BUDGET_MS} ms disponibles, à ${fmtTps(level.ticksPerSecond)} ticks par seconde.`,
  };
}

/**
 * État et voyant d'un serveur. Le voyant est celui de son monde le plus mal en point, aggravé par ce
 * qui s'est passé dans la dernière heure (ticks trop longs, monde figé).
 *
 * @param now          horloge du satellite
 * @param timeZone     fuseau des heures citées (tests) ; omis : celui du navigateur
 * @param graceSeconds premières secondes après le démarrage pendant lesquelles le serveur n'est pas jugé
 */
export function serverHealth(server: ServerSummary, now: number, timeZone?: string, graceSeconds: number = DEFAULT_STARTUP_GRACE_S): Health {
  const state = stateOf(server, now);
  if (state === 'off') {
    return {
      state, light: 'none', level: null, title: 'Profileur éteint',
      reason: "Le profileur n'est pas allumé sur ce serveur. Le serveur, lui, donne de ses nouvelles.",
    };
  }
  if (state === 'paused') {
    return {
      state, light: 'none', level: null, title: 'Mesures en pause',
      reason: 'Les mesures ont été suspendues sur ce serveur (commande « profiler off »). « profiler on » les reprend.',
    };
  }
  if (state === 'stale') {
    const last = server.lastSnapshotAt ?? server.lastSeenAt;
    // « à 14:02 » le jour même, « le 13/10 à 14:02 » sinon.
    const when = last ? fmtWhen(last, now, timeZone) : null;
    return {
      state, light: 'none', level: null, title: 'Plus de données',
      reason: `Dernier message reçu ${when == null ? 'à une date inconnue' : when.includes(' à ') ? `le ${when}` : `à ${when}`}. `
        + 'Le serveur est peut-être arrêté, ou le mod retiré : ce n\'est pas un diagnostic de panne.',
    };
  }

  // Les mesures arrivent, mais le serveur vient de démarrer : pas de verdict, ni vert ni rouge.
  if (startingUp(server, now, graceSeconds)) {
    const minutes = Math.max(1, Math.round(graceSeconds / 60));
    return {
      state, light: 'none', level: null, title: 'Démarrage en cours',
      reason: `Démarré à ${fmtClock(server.startedAt!, timeZone)} : pendant ses ${minutes} premières minutes le serveur charge son monde, `
        + `ses ticks longs ne sont pas jugés. Verdict à partir de ${fmtClock(server.startedAt! + graceSeconds * 1000, timeZone)}.`,
    };
  }

  // measuring : le pire des mondes, sur la dernière minute.
  let worst: { light: Light; title: string; reason: string } | null = null;
  let worstLevel: string | null = null;
  for (const level of server.levels) {
    const h = levelHealth(level);
    if (!worst || RANK[h.light] > RANK[worst.light]) {
      worst = h;
      worstLevel = level.level;
    }
  }
  if (!worst) {
    return { state, light: 'none', level: null, title: 'Aucun monde', reason: "Le serveur répond, mais aucun monde n'est chargé." };
  }

  // Dernière heure : ce que la dernière minute ne montre plus. Ces compteurs portent sur tout le
  // serveur : le verdict qu'ils donnent n'est attribué à aucun monde.
  let fromHour = false;
  if (worst.light !== 'bad') {
    const frozen = server.maxTickMsLastHour != null && server.maxTickMsLastHour >= THRESHOLDS.badFreezeMs;
    if (frozen) {
      fromHour = true;
      worst = {
        light: 'bad', title: 'Monde figé récemment',
        reason: `Un tick a duré ${fmtMs(server.maxTickMsLastHour)} dans la dernière heure : le monde est resté figé tout ce temps.`,
      };
    } else if (server.spikesLastHour >= THRESHOLDS.badSpikesPerHour) {
      fromHour = true;
      worst = {
        light: 'bad', title: 'Accroches répétées',
        reason: `${plural(server.spikesLastHour, 'tick trop long', 'ticks trop longs')} dans la dernière heure`
          + `${server.maxTickMsLastHour != null ? ` (le plus long : ${fmtMs(server.maxTickMsLastHour)})` : ''}.`,
      };
    } else if (worst.light === 'ok' && server.spikesLastHour > 0) {
      fromHour = true;
      worst = {
        light: 'watch', title: 'Accroche récente',
        reason: `${plural(server.spikesLastHour, 'tick trop long', 'ticks trop longs')} dans la dernière heure`
          + `${server.maxTickMsLastHour != null ? ` (le plus long : ${fmtMs(server.maxTickMsLastHour)})` : ''}. Depuis, tout va bien.`,
      };
    }
  }
  return { state, light: worst.light, title: worst.title, reason: worst.reason, level: server.levels.length > 1 && !fromHour ? worstLevel : null };
}

/** Serveur connu de la Console qui n'a jamais rien envoyé. */
export const NEVER_SEEN: Health = {
  state: 'never', light: 'none', level: null, title: 'Aucune donnée reçue',
  reason: "Ce serveur n'a jamais envoyé de mesure : mod compagnon absent, profileur éteint au démarrage, ou moteur d'avant la 0.1.48.",
};

/** Le monde qui décide de l'affichage d'un serveur dans la liste : le plus mal en point, sinon le plus chargé. */
export function headlineLevel(server: ServerSummary): LevelSummary | null {
  let best: LevelSummary | null = null;
  let bestRank = -1;
  for (const level of server.levels) {
    const rank = RANK[levelHealth(level).light];
    if (!best || rank > bestRank || (rank === bestRank && level.averageTickMs > best.averageTickMs)) {
      best = level;
      bestRank = rank;
    }
  }
  return best;
}

/** Part du budget de 50 ms prise par une durée, de 0 à 1 (bornée : la jauge ne déborde pas). */
export function budgetShare(ms: number | null | undefined): number {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return 0;
  return Math.min(1, ms / TICK_BUDGET_MS);
}

/** Phrase « 5 % du budget » : la durée face aux 50 ms, en clair. */
export function budgetText(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '-';
  const pct = (ms / TICK_BUDGET_MS) * 100;
  if (pct > 0 && pct < 1) return 'moins de 1 % du budget';
  return `${fmtNumber(pct, 0)} % du budget`;
}

/** Ordre de la liste : les serveurs en difficulté d'abord, puis à surveiller, puis sains, puis sans mesure. */
export function compareByUrgency(a: { health: Health; name: string }, b: { health: Health; name: string }): number {
  const order: Record<Light, number> = { bad: 0, watch: 1, ok: 2, none: 3 };
  return order[a.health.light] - order[b.health.light] || a.name.localeCompare(b.name, 'fr', { numeric: true, sensitivity: 'base' });
}
